import type { TaskView, WorkflowView } from '@/lib/api/generated/model';

import { buildBoard, type BoardStepSection } from './boardModel';
import { locateTask, stepLabelsOf, type TaskLocation } from './locateTask';

/** Where a task stands, and the names of its workflow's steps. */
export interface TaskPlacement {
  task: TaskView | undefined;
  workflow: WorkflowView | undefined;
  /** `undefined` for an orphan, or when the task's board cannot be built. */
  location: TaskLocation | undefined;
  /** Every step of the task's workflow by id; empty when there is no board. */
  stepLabels: Map<string, string>;
  /** Every step in board order, for the drawer's next action (spec 125); empty without a board. */
  steps: BoardStepSection[];
}

/**
 * What the task drawer needs about one task, from the workspace aggregate
 * alone — so a page other than the board can host the drawer (spec 111).
 *
 * One finished answer rather than `buildBoard`, `locateTask` and
 * `stepLabelsOf` exported separately, for the reason `listWorkspaceTasks`
 * gives: the parts stay inside this package, and a caller cannot assemble them
 * differently from the board.
 *
 * Built from **every** task of the workflow, never a narrowed list — the
 * board locates its open task against the unfiltered board for the same
 * reason: a filter must not make an open task look unplaced.
 */
export function taskPlacement(
  workflows: readonly WorkflowView[],
  tasks: readonly TaskView[],
  taskId: string,
): TaskPlacement {
  const task = tasks.find((candidate) => candidate.id === taskId);
  const workflow = task
    ? workflows.find((candidate) => candidate.id === task.workflow_id)
    : undefined;
  if (!task || !workflow) {
    return {
      task,
      workflow,
      location: undefined,
      stepLabels: new Map(),
      steps: [],
    };
  }

  const result = buildBoard(
    workflow,
    tasks.filter((candidate) => candidate.workflow_id === workflow.id),
  );
  if (!result.ok) {
    return {
      task,
      workflow,
      location: undefined,
      stepLabels: new Map(),
      steps: [],
    };
  }

  return {
    task,
    workflow,
    location: locateTask(result.board, taskId),
    stepLabels: stepLabelsOf(result.board),
    steps: result.board.columns.flatMap((column) => column.steps),
  };
}
